import gql from 'graphql-tag';

/**
 * Documents cotisation propres à primapp-web.
 *
 * La mutation partagée de `primapp-common` ne sélectionne que `reference`, et elle est
 * commune au web, au natif et à l'embed : l'enrichir obligerait à republier le paquet et à
 * redéployer les trois fronts pour un besoin qui n'est, aujourd'hui, que celui d'ici. On
 * garde donc une copie locale, comme l'embed le fait dans `services/masterApi.js`.
 */

/*
 * CE QUE LA SOUSCRIPTION A RÉELLEMENT FAIT.
 *
 * `setMembershipSchedule` est IDEMPOTENT : si un échéancier existe déjà pour le couple
 * (golf, référence, année, compte), rien n'est créé ni modifié - c'est ce qui protège du
 * double prélèvement. La mutation renvoyait alors un succès à vide, indiscernable d'une
 * vraie création, et l'écran délivrait un bon bâti sur ce qu'il venait de DEMANDER.
 *
 * Mesuré le 02/08/2026 sur primapp-embed, dont le code était identique : bon pour 2 300 €
 * en 10 × 230 à partir du 03/08, alors que l'échéancier réel valait 2 200 € en 10 × 220,
 * `workflow_state` `locked`, donc jamais prélevé. Correctif serveur `39b680ee` (master) et
 * `230a6f2` (primapp-api), déployés le 02/08 à 15:03 et 15:04.
 *
 * `amount` est ici sélectionnable sans risque : primapp-api le déclare `Int` NULLABLE
 * (vérifié par introspection sur `api-app.prima.golf`), au contraire de master qui le
 * déclare `Int!`. C'est d'ailleurs ce qui a cassé la création côté master jusqu'au
 * correctif `6004f296` : la branche de création ne le renvoyait pas.
 */
export const SET_MEMBERSHIP_SCHEDULE = gql`
  mutation SetMembershipSchedule(
    $amount: Int!
    $year: String!
    $title: String!
    $golfId: String!
    $reference: String!
    $creditCardId: String!
    $paymentSchedule: [MembershipScheduleItem]!
    $skipWS: Boolean
    $accountId: String
  ) {
    setMembershipSchedule(
      year: $year
      title: $title
      amount: $amount
      golfId: $golfId
      skipWS: $skipWS
      accountId: $accountId
      reference: $reference
      creditCardId: $creditCardId
      paymentSchedule: $paymentSchedule
    ) {
      reference
      # created / existing / cardUpdated. Sur les deux derniers, les champs qui
      # suivent decrivent l'echeancier STOCKE, pas celui qui vient d'etre demande.
      status
      workflowState
      amount
      year
      frequency
      paymentSchedule {
        date
        amount
      }
    }
  }
`;

/*
 * L'ÉCHÉANCIER RÉELLEMENT STOCKÉ, LU AVANT D'EN PROPOSER UN.
 *
 * `getExercises` vient du PGB du golf, et son `paymentSchedule` peut être vide alors qu'un
 * échéancier existe chez nous : mesuré le 02/08/2026 sur un compte qui en portait un depuis
 * le 30/06. La liste proposait donc « Payer en N mensualités » pour un plan déjà en place,
 * et le golfeur ne l'apprenait qu'au dernier clic.
 *
 * `checkMembershipSchedule` vit sur MASTER et n'est pas déclarée dans le schéma de
 * primapp-api : elle s'interroge avec `masterClient`. C'est déjà ce que font les écrans
 * publics `ConfirmMembership` et `PayMembership`.
 *
 * `workflowState` est demandé bien que le résolveur ne le renvoie pas encore : le type le
 * déclare et il est nullable, donc la requête passe, et le jour où master le remplira
 * l'écran dira aussi qu'un plan est suspendu. En attendant on n'affirme rien là-dessus.
 */
export const CHECK_MEMBERSHIP_SCHEDULE = gql`
  query CheckMembershipSchedule($golfId: String!, $accountId: String!, $reference: String!) {
    checkMembershipSchedule(golfId: $golfId, accountId: $accountId, reference: $reference) {
      reference
      year
      amount
      frequency
      workflowState
      paymentSchedule {
        date
        amount
        status
      }
    }
  }
`;

/**
 * Rend l'échéancier stocké, ou `null` s'il n'y en a pas.
 *
 * Ne rejette jamais : une relecture indisponible ne doit pas empêcher d'afficher la liste
 * des cotisations, ni de souscrire. Dans le doute, on retombe sur le comportement d'avant.
 */
export const checkMembershipSchedule = (client, variables) =>
  client
    .query({ query: CHECK_MEMBERSHIP_SCHEDULE, variables, fetchPolicy: 'no-cache' })
    .then((res) => (res.data && res.data.checkMembershipSchedule) || null)
    .catch(() => null);

/**
 * L'échéancier stocké concerne-t-il BIEN la cotisation affichée ?
 *
 * Deux pièges, déjà traités de la même façon dans `Public/ConfirmMembership` :
 *   - la référence est réutilisée d'une saison à l'autre, d'où la comparaison de l'année ;
 *   - un paiement comptant crée une ligne `single`, qui n'est pas un plan de prélèvement.
 */
export const isScheduleForMembership = (stocke, membership) =>
  Boolean(
    stocke &&
      stocke.reference &&
      stocke.frequency !== 'single' &&
      String(stocke.year) === String(membership.year),
  );

/** Même forme de retour que `Membership.api.setMembershipSchedule`. */
export const setMembershipSchedule = (client, variables) =>
  client
    .mutate({ mutation: SET_MEMBERSHIP_SCHEDULE, variables })
    .then((res) => ({ membershipSchedule: res.data && res.data.setMembershipSchedule }));

export default {
  SET_MEMBERSHIP_SCHEDULE,
  setMembershipSchedule,
  CHECK_MEMBERSHIP_SCHEDULE,
  checkMembershipSchedule,
  isScheduleForMembership,
};
